import type {
  AssignModulesInput,
  AssignRolesInput,
  CreateStaffInput,
  DataResponse,
  ListStaffQuery,
  ModuleCode,
  PaginatedResponse,
  PermissionCode,
  RoleCode,
  UpdateStaffInput,
  UpdateStaffProfileInput,
  User,
  UserProfile,
} from '@pms/shared';
import { api } from '@/lib/api';
import { downloadXlsx, localFileStamp } from '@/lib/download';

export interface StaffListItem extends User {
  roles: RoleCode[];
  department_name: string | null;
  designation_name: string | null;
  avatar_url: string | null;
  photo_url: string | null;
  new_photos_url: string | null;
  city: string | null;
  joining_date: string | null;
}

export interface StaffDetail {
  user: User;
  profile: UserProfile | null;
  photo_url: string | null;
  new_photos_url: string | null;
  roles: RoleCode[];
  permissions: PermissionCode[];
  modules: ModuleCode[];
}

export interface PickerUser {
  id: string;
  employee_code: string | null;
  first_name: string;
  last_name: string;
  department_name: string | null;
}

/** The API's per-request ceiling — paginationQuerySchema caps `limit` at 100. */
const MAX_PAGE_LIMIT = 100;

export const usersService = {
  async list(query: Partial<ListStaffQuery> = {}): Promise<PaginatedResponse<StaffListItem>> {
    const res = await api.get<PaginatedResponse<StaffListItem>>('/users', { params: query });
    return res.data;
  },

  /**
   * Every active staff member, walking as many pages as the API's 100-per-request
   * ceiling requires.
   *
   * Assignment pickers need the whole roster. A single `list({ limit: 100 })` call
   * looks complete but silently truncates once the org passes 100 users — and
   * because /users sorts by `created_at DESC`, the rows it drops are the
   * longest-serving staff, who are exactly the people most likely to hold an asset.
   */
  async listAllActive(): Promise<StaffListItem[]> {
    const first = await this.list({ page: 1, limit: MAX_PAGE_LIMIT, is_active: true });
    if (first.pagination.totalPages <= 1) return first.data;
    const rest = await Promise.all(
      Array.from({ length: first.pagination.totalPages - 1 }, (_, i) =>
        this.list({ page: i + 2, limit: MAX_PAGE_LIMIT, is_active: true })),
    );
    return rest.reduce((acc, r) => acc.concat(r.data), first.data);
  },

  /**
   * Downloads the filtered staff register as a styled .xlsx. Sends the same
   * params as list() — the backend ignores page/limit and returns every match,
   * including deactivated staff when the status filter allows them.
   */
  async exportXlsx(query: Partial<ListStaffQuery> = {}): Promise<void> {
    await downloadXlsx(
      api.get('/users/export/xlsx', { params: query, responseType: 'blob' }),
      `staff-${localFileStamp()}.xlsx`,
    );
  },

  /**
   * Downloads the live leave credit sheet (every matching staff × every active
   * leave type) as .xlsx. Same params and staff set as exportXlsx(); the backend
   * reads balances at request time, so every click is current.
   */
  async exportLeaveBalancesXlsx(query: Partial<ListStaffQuery> = {}): Promise<void> {
    await downloadXlsx(
      api.get('/users/export/leave-balances/xlsx', { params: query, responseType: 'blob' }),
      `leave-balances-${localFileStamp()}.xlsx`,
    );
  },

  async picker(): Promise<PickerUser[]> {
    const res = await api.get<{ data: PickerUser[] }>('/users/picker');
    return res.data.data;
  },

  async get(id: string): Promise<StaffDetail> {
    const res = await api.get<DataResponse<StaffDetail>>(`/users/${id}`);
    return res.data.data;
  },

  async create(input: CreateStaffInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/users', input);
    return res.data.data;
  },

  async update(id: string, input: UpdateStaffInput): Promise<void> {
    await api.patch(`/users/${id}`, input);
  },

  async deactivate(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async assignRoles(id: string, input: AssignRolesInput): Promise<void> {
    await api.post(`/users/${id}/roles`, input);
  },

  async setModules(id: string, input: AssignModulesInput): Promise<void> {
    await api.post(`/users/${id}/modules`, input);
  },

  async removeModule(id: string, moduleCode: ModuleCode): Promise<void> {
    await api.delete(`/users/${id}/modules/${moduleCode}`);
  },

  async resetModulesToDefaults(id: string): Promise<void> {
    await api.post(`/users/${id}/modules/reset-to-defaults`);
  },

  async updateStaffProfile(id: string, input: UpdateStaffProfileInput): Promise<void> {
    await api.patch(`/users/${id}/profile`, input);
  },

  async setPassword(id: string, new_password: string): Promise<void> {
    await api.post(`/users/${id}/password`, { new_password });
  },
};
